import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Button, Divider, Form, Input, Select, Spin, Tag } from "antd";
import {
  EntityStatus,
  ConfigActions,
  ConfigEntity,
  formItemLayout,
  tailItemLayout,
  ConfigJSON,
} from "../../../Constants/Constants";
import {
  jsonValidator,
  selectFilterFunction,
} from "../../../Constants/Utilities";
import { onFormSubmit } from "../../../ApiHandlers/ConfigApiHandlers";
import { getConfigFormTitle } from "../../../Helpers/ConfigHelpers";
import { DiscardWarning } from "../../Common/DiscardWarning";
import { BreadCrumbs } from "../../Common/BreadCrumbs";

const getInitialValues = (record) => {
  const initialValues = {
    status: EntityStatus.ACTIVE,
    config: ConfigJSON,
  };
  if (record) {
    initialValues.name = record.name;
    initialValues.status = record.status;
    initialValues.providerId = record.providerId;
    initialValues.config = JSON.stringify(record.config, null, 4);
  }
  return initialValues;
};

const ServiceProviderConfigForm = (props) => {
  console.log("ServiceProviderConfigForm props", props);
  const { action, record, onActionClick, providerData, providerDataLoading } =
    props;

  const title = useMemo(() => getConfigFormTitle(action), [action]);
  const [initialValues, setInitialValues] = useState(getInitialValues(record));

  const [form] = Form.useForm();

  const [processing, setProcessing] = useState(false);
  const isAddForm = action === ConfigActions.add;
  const isViewForm = action === ConfigActions.view;

  const onBackClick = () => {
    const isChanged =
      action === ConfigActions.view ? false : checkForInputChanges();
    DiscardWarning({ isChanged, onActionClick })();
  };

  const checkForInputChanges = () => {
    const fieldsToCheck = [
      "name",
      "providerId",
      "groupId",
      "deviceId",
      "serviceId",
      "config",
      "status",
    ];
    const values = form.getFieldsValue();
    let isInputsChanged = false;
    fieldsToCheck.every((f) => {
      if (
        values.hasOwnProperty(f) &&
        values[f] !== initialValues[f] &&
        (!!values[f] || !!initialValues[f])
      ) {
        isInputsChanged = true;
        return false;
      }
      return true;
    });
    return isInputsChanged;
  };

  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(
            `${title} Service Provider Configuration`
          )}
          onParentActionClick={onBackClick}
        />
      </div>
      <Divider className="divider" />
      <div className="form-data-container">
        <fieldset disabled={isViewForm}>
          <Form
            form={form}
            layout="horizontal"
            name="service-provider-config-form"
            onFinish={onFormSubmit({
              props,
              setInitialValues,
              setProcessing,
              entity: ConfigEntity.provider,
            })}
            initialValues={initialValues}
            requiredMark={"optional"}
            size={"middle"}
            {...formItemLayout}
          >
            <Spin spinning={processing} tip={"Processing, please wait..."}>
              <Form.Item
                name="name"
                label="Configuration Name"
                rules={[
                  { required: true, message: "Please fill in this field" },
                ]}
              >
                <Input
                  title="Name should be unique"
                  style={{ width: "90%", maxWidth: "300px" }}
                  disabled={!isAddForm}
                />
              </Form.Item>

              <Form.Item
                name="providerId"
                label="Service Provider:"
                rules={[
                  { required: true, message: "Please select Service Provider" },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "90%", maxWidth: "350px" }}
                  loading={providerDataLoading}
                  disabled={providerDataLoading || !isAddForm}
                  filterOption={selectFilterFunction}
                  placeholder={
                    providerDataLoading
                      ? "Loading Service Provider list..."
                      : "Select Service Provider"
                  }
                >
                  {providerData.map((item) => (
                    <Select.Option
                      key={item.providerId}
                      title={item.providerName}
                    >
                      {item.providerName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="config"
                label="Configuration"
                validateTrigger={["onBlur"]}
                rules={[{ required: true, validator: jsonValidator() }]}
              >
                <Input.TextArea
                  title="Configuration must be JSON object"
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  style={{ width: "90%", maxWidth: "500px" }}
                />
              </Form.Item>

              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true }]}
              >
                <Tag
                  color={
                    initialValues.status === EntityStatus.ACTIVE
                      ? "green"
                      : "red"
                  }
                >
                  {initialValues.status}
                </Tag>
              </Form.Item>

              {!isViewForm && (
                <Form.Item {...tailItemLayout}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-button"
                  >
                    {title}
                  </Button>
                  <Button
                    htmlType="button"
                    onClick={() => form.resetFields()}
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                </Form.Item>
              )}
            </Spin>
          </Form>
        </fieldset>
      </div>
    </div>
  );
};

ServiceProviderConfigForm.propTypes = {
  action: PropTypes.string,
  record: PropTypes.object,
  providerData: PropTypes.array,
  providerDataLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  onActionClick: PropTypes.func,
  parentBread: PropTypes.array,

  configManagementApi: PropTypes.object,
};

const mapStateToProps = (state) => ({
  configManagementApi: state.apis.configManagementApi,
});
const connectedServiceConfigForm = connect(mapStateToProps)(
  ServiceProviderConfigForm
);
export default withRouter(connectedServiceConfigForm);
